import React, { useMemo, useState } from 'react'
import { useMediaQuery, useTheme } from '@material-ui/core'
import { Button } from '@resiliatech/birdsong'
import { useAppState } from 'Context/AppContext/AppContext'
import {
  ExpandLess as ViewLessIcon,
  ExpandMore as ViewMoreIcon,
} from '@material-ui/icons'
import { Snackbar } from '@mui/material'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import { ChecklistItemWithResourceInfo } from 'TypeDefs/Goal'
import GET_ACADEMY_RESOURCES from 'ApolloAcademy/Queries/GetAcademyResources'
import { GOALS } from 'Apollo/Queries/Goals'
import UPSERT_CHECKLIST_ITEM from 'Apollo/Mutations/UpsertChecklistItem'
import DELETE_CHECKLIST_ITEM from 'Apollo/Mutations/DeleteChecklistItem'
import { useMutation, useQuery } from '@apollo/client'
import { analytics } from 'Modules/Analytics'
import { initiatives } from 'Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { goals } from 'Modules/Analytics/trackActionInterface/Impact/Goals'
import { ChecklistItem, GoalType } from 'Generated/graphql-types'
import { ACADEMY_CONTEXT } from 'Components/Pages/Academy/_common/utils'
import {
  ResourceSortOptions,
  ResourceType,
} from 'Generated/academy-graphql-types'
import useNonprofitOrgTeam from 'Hooks/useNonprofitOrgTeam/useNonprofitOrgTeam'
import useInitiativeStyles from '../InitiativesContainer/styles'
import TaskItem from './TaskItem'
import { TaskModal } from './TaskModal/TaskModal'
import { DeleteTaskModal } from './TaskModal/DeleteTaskModal'
import {
  Header,
  HeaderContainer,
  TaskItemsContainer,
  TaskListContainer,
  ViewMoreOrLessButton,
} from './TaskList.styled'
import { GoalSectionEmptyState } from '../../../Impact/GoalPage/EmptyStates/GoalSectionEmptyState'

const MAX_INITIAL_TASKS = 5

type Props = {
  items: ChecklistItem[]
  eventProps: {
    goalType: GoalType
    templateLibraryId: number | null
    initiativeId?: number
    goalId: number
    isGoalSharedWithFunder?: boolean
  }
  simple?: boolean
}

const TaskList = ({ items, eventProps, simple }: Props) => {
  const initiativeStyles = useInitiativeStyles()
  const { teamMap = {} } = useNonprofitOrgTeam()
  const theme = useTheme()
  const isScreenLarge = useMediaQuery(theme.breakpoints.up('md'))
  const {
    ui: { appPanel },
    userConfig: { id: userId },
    nonprofitOrgConfig: { orgId: nonprofitOrgId },
  } = useAppState()
  const [viewMore, setViewMore] = useState(true)
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [showDeleteTaskModal, setShowDeleteTaskModal] = useState(false)
  const [activeTask, setActiveTask] = useState<ChecklistItemWithResourceInfo>()
  const [showSnackbar, setShowSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')

  const { goalType, templateLibraryId, goalId, initiativeId } = eventProps
  const eventData = {
    userId,
    goalId,
    initiativeId,
    goalType,
    templateLibraryId,
  }
  /*
    Get all the resource ids for the checklist items then load each checklist item with all necessary
    information to render it with additional details instead of passing multiple separate props
   */
  const getResourceIdsForItems = () =>
    items
      .map((checklistItem: ChecklistItem) => checklistItem.resourceId)
      .filter((resourceId) => resourceId !== null)

  const { data, loading } = useQuery(GET_ACADEMY_RESOURCES, {
    ...ACADEMY_CONTEXT,
    variables: {
      ids: getResourceIdsForItems(),
      resourceType: [
        ResourceType.Article,
        ResourceType.Course,
        ResourceType.Template,
        ResourceType.LearningTrack,
      ],
      query: '',
      sort: ResourceSortOptions.Random,
      anonymousId: global.analytics?.user?.()?.anonymousId?.(),
    },
    skip: !getResourceIdsForItems().length,
  })

  const taskItemsWithDetails = useMemo(() => {
    const getResourceInfoForItem = (resourceId: string) =>
      data.resources.nodes.find(
        (itemResource: { id: string }) =>
          itemResource?.id.toLowerCase() === resourceId.toLowerCase()
      )

    return items.map((checklistItem) => {
      const { id: ownerId = null } = checklistItem.owner || {}
      const owner = ownerId ? teamMap[ownerId] : null
      const newItemInfo: ChecklistItemWithResourceInfo = {
        ...checklistItem,
        createdAt: checklistItem.createdAt,
        resource:
          data && checklistItem.resourceId
            ? getResourceInfoForItem(checklistItem.resourceId)
            : null,
        isCompleted: checklistItem.isCompleted || false,
        linkText: checklistItem.linkText || null,
        linkUrl: checklistItem.linkUrl || null,
        owner: {
          id: checklistItem.owner?.id,
          firstName: owner?.firstName || '',
          lastName: owner?.lastName || '',
        },
      }
      return newItemInfo
    })
  }, [items, data, teamMap])

  const [upsertChecklistItem] = useMutation(UPSERT_CHECKLIST_ITEM)

  const calculateCompletionPercentage = (tasks: ChecklistItem[]) => {
    const totalTasks = tasks.length
    const completedTasks = tasks.filter((item) => item.isCompleted).length

    const completionPercentage = (completedTasks / totalTasks) * 100

    // Calculate the exact halfway completion percentage
    const halfwayIndex = Math.ceil(totalTasks / 2)
    const halfwayCompletedPercentage = (halfwayIndex / totalTasks) * 100

    return {
      completionPercentage,
      halfwayCompletedPercentage,
    }
  }

  const saveItem = async (
    itemInfoToSave?: Omit<
      ChecklistItemWithResourceInfo,
      'createdAt' | 'updatedAt'
    >,
    toastMessage?: string
  ) => {
    const itemInfo = {
      dueDate: itemInfoToSave?.dueDate || undefined,
      goalId,
      id: itemInfoToSave?.id,
      isCompleted: itemInfoToSave?.isCompleted,
      linkText: itemInfoToSave?.linkText,
      linkUrl: itemInfoToSave?.linkUrl,
      ownerId: itemInfoToSave?.owner?.id || null,
      resourceId: itemInfoToSave?.resource?.id ?? null,
      title: itemInfoToSave?.title || '',
    }

    const { data: taskData } = await upsertChecklistItem({
      variables: {
        itemInfo,
        nonprofitOrgId,
      },
      refetchQueries: [GOALS],
    })

    const updatedTasks = items.map((item) =>
      item.id === itemInfoToSave?.id
        ? {
            ...item,
            isCompleted:
              itemInfoToSave.isCompleted !== undefined
                ? itemInfoToSave.isCompleted
                : true,
          }
        : item
    )

    const { completionPercentage, halfwayCompletedPercentage } =
      calculateCompletionPercentage(updatedTasks)

    if (
      completionPercentage === halfwayCompletedPercentage &&
      itemInfoToSave?.isCompleted === true &&
      items.length >= 5
    ) {
      analytics.trackActionCreator(goals.Goal_Tasks_Halfway_Completed)({
        ...eventData,
        goalUpdatedDat: new Date().toISOString(),
        goalProgress: completionPercentage,
      })
    } else if (completionPercentage >= 100) {
      analytics.trackActionCreator(goals.Goal_Tasks_All_Completed)({
        ...eventData,
        goalUpdatedDat: new Date().toISOString(),
        goalProgress: completionPercentage,
      })
    }

    if (toastMessage) popToast(toastMessage)
    return taskData?.upsertChecklistItem?.id
  }

  const toggleView = () => {
    setViewMore((currentView) => !currentView)
  }

  const [deleteChecklistItem] = useMutation(DELETE_CHECKLIST_ITEM, {
    refetchQueries: [
      { query: GOALS, variables: { activeOnly: true, nonprofitOrgId } },
    ],
    awaitRefetchQueries: false,
  })

  const popToast = (message: string) => {
    setSnackbarMessage(message)
    setShowSnackbar(true)
  }

  const deleteItem = async (itemId: number) => {
    await deleteChecklistItem({
      variables: {
        id: itemId,
        nonprofitOrgId,
      },
    })
    setShowDeleteTaskModal(false)
    setActiveTask(undefined)
    popToast('Your task was deleted')
  }

  const handleDuplicateTask = async (item: ChecklistItemWithResourceInfo) => {
    analytics.trackActionCreator(initiatives.Goal_Task_DuplicateTask_Clicked)({
      ...eventData,
      taskDescription: item.title,
      taskId: item.id,
      taskStatus: item.isCompleted ? 'complete' : 'pending',
      taskUpdatedDate: item.updatedAt.substring(0, 10),
      dueDate: item.dueDate,
      resourceId: item.resource?.id,
      linkText: item.linkText,
      linkUrl: item.linkUrl,
    })
    setActiveTask({ ...item, owner: null, id: undefined })
    setShowTaskModal(true)
  }

  const handleTask = (task?: ChecklistItemWithResourceInfo) => {
    if (task) {
      setActiveTask(task)
      analytics.trackActionCreator(initiatives.Goal_Task_EditTask_Clicked)({
        ...eventData,
        taskDescription: task.title,
        taskId: task.id,
        taskStatus: task.isCompleted ? 'complete' : 'pending',
        taskUpdatedDate: task.updatedAt.substring(0, 10),
        dueDate: task.dueDate,
        resourceId: task.resource?.id,
        linkText: task.linkText,
        linkUrl: task.linkUrl,
      })
    } else {
      analytics.trackActionCreator(initiatives.Goal_Add_Task_Clicked)(eventData)
    }

    setShowTaskModal(true)
  }

  const handleCloseTaskModal = () => {
    setShowTaskModal(false)
    setActiveTask(undefined)
  }

  const handleClickDeleteTask = async (task: ChecklistItemWithResourceInfo) => {
    analytics.trackActionCreator(initiatives.Goal_Task_DeleteTask_Clicked)({
      ...eventData,
      taskDescription: task.title,
      taskId: task.id,
      taskStatus: task.isCompleted ? 'complete' : 'pending',
      taskUpdatedDate: task.updatedAt.substring(0, 10),
      dueDate: task.dueDate,
      resourceId: task.resource?.id,
      linkText: task.linkText,
      linkUrl: task.linkUrl,
    })
    setActiveTask(task)
    setShowDeleteTaskModal(true)
  }

  const handleCloseDeleteTaskModal = () => {
    setShowDeleteTaskModal(false)
    setActiveTask(undefined)
  }

  const handleCloseSnackbar = () => {
    setShowSnackbar(false)
    setSnackbarMessage('')
  }

  const sortByIdDescending = (
    a: ChecklistItemWithResourceInfo,
    b: ChecklistItemWithResourceInfo
  ) => {
    if (a.id && b.id) return b.id - a.id
    return 0
  }

  const sortByIdAscending = (
    a: ChecklistItemWithResourceInfo,
    b: ChecklistItemWithResourceInfo
  ) => {
    if (a.id && b.id) return a.id - b.id
    return 0
  }

  const customSort = (
    a: ChecklistItemWithResourceInfo,
    b: ChecklistItemWithResourceInfo
  ) => {
    if (goalType === GoalType.Template) return sortByIdAscending(a, b)

    return sortByIdDescending(a, b)
  }

  const hasTasks = taskItemsWithDetails.length > 0
  const areTasksBelowTheFold = taskItemsWithDetails.length > MAX_INITIAL_TASKS

  return (
    <TaskListContainer>
      <Snackbar
        open={showSnackbar}
        className={initiativeStyles.savedSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={snackbarMessage}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
      />
      {showDeleteTaskModal && activeTask?.id && (
        <DeleteTaskModal
          isOpen={showDeleteTaskModal}
          onClose={handleCloseDeleteTaskModal}
          onDeleteTask={deleteItem}
          taskId={activeTask.id}
        />
      )}
      {showTaskModal && (
        <TaskModal
          goalId={goalId}
          isOpen={showTaskModal}
          onClose={handleCloseTaskModal}
          onSubmitTask={saveItem}
          task={activeTask}
          eventProps={eventProps}
        />
      )}
      {simple ? (
        <HeaderContainer>
          <Button
            isDisabled={appPanel}
            label='Add Task'
            variant='primary'
            onClick={() => handleTask()}
          />
        </HeaderContainer>
      ) : (
        <HeaderContainer>
          <Header>Task List</Header>
          {isScreenLarge && (
            <Button
              isDisabled={appPanel}
              label='Add Task'
              variant='primary'
              onClick={() => handleTask()}
            />
          )}
        </HeaderContainer>
      )}
      {!hasTasks && (
        <GoalSectionEmptyState
          icon={<CheckBoxOutlinedIcon />}
          message='Add a task to start progressing towards your goal'
        />
      )}
      {hasTasks && (
        <TaskItemsContainer data-testid='itemsContainer'>
          {taskItemsWithDetails.sort(customSort).map((item, index) => {
            if (viewMore || index < MAX_INITIAL_TASKS) {
              return (
                <TaskItem
                  eventProps={eventProps}
                  item={item}
                  key={item.id}
                  isResourceLoading={loading}
                  onChange={saveItem}
                  onDeleteTask={handleClickDeleteTask}
                  onDuplicateTask={handleDuplicateTask}
                  onEditTask={() => handleTask(item)}
                />
              )
            }
            return null
          })}
        </TaskItemsContainer>
      )}
      {areTasksBelowTheFold && (
        <ViewMoreOrLessButton onClick={toggleView}>
          View {!viewMore ? 'More' : 'Less'}{' '}
          {!viewMore ? <ViewMoreIcon /> : <ViewLessIcon />}
        </ViewMoreOrLessButton>
      )}
    </TaskListContainer>
  )
}
export default TaskList
